import { useEffect, useState } from 'react';
import {
  ActionIcon, Badge, Button, Group, Modal, NumberInput, SegmentedControl, Stack,
  Table, Text, TextInput, Alert,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconPlus, IconTrash, IconScale, IconInfoCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { SectionCard } from './SectionCard';
import { formatSom, tzDateKey } from '../lib/format';
import { uzDateProps } from '../lib/dateInput';
import { confirmDelete } from '../lib/confirm';

/** Server sanasi ISO ("2026-10-09T19:00:00.000Z" — Toshkent yarim tuni) yoki "YYYY-MM-DD" → "10.10.2026". */
const kunMatni = (d: string) => {
  const k = tzDateKey(d);
  return `${k.slice(8, 10)}.${k.slice(5, 7)}.${k.slice(0, 4)}`;
};

interface Tuzatish {
  id: string; date: string; amount: number; reason: string;
  createdBy?: { fullName: string } | null;
}

const kunKalit = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * KUNLIK JARIMA VA RAG'BAT.
 *
 * Fiks oylik maosh kech kelgan yoki erta ketgan xodimga ta'sir qilmasdi, yaxshi
 * ishlagani ham bilinmasdi. Bu yerdan admin ANIQ KUNGA summa qo'shadi yoki
 * ayiradi — natija o'sha oyning hisobiga darhol kiradi.
 *
 * Ikkalasi BITTA ro'yxatda: farqi faqat ishorada, savol esa bitta — "o'sha kun
 * uchun maosh qancha bo'ldi". Alohida jadval qilinsa, admin ikki joyga qarab
 * qo'shib-ayirib yurishi kerak bo'lardi.
 */
export function SalaryAdjustments({
  userId, month, kunlik, taklif, onTaklifTugadi,
}: {
  userId: string;
  month: string;
  kunlik?: number;
  /** Oynani tayyor holda ochish (ish vaqti jadvalidagi "Bonus"): sana, izoh; `key` — har bosishda yangi. */
  taklif?: { kun: string; sabab: string; key: number } | null;
  /** Oyna yopilganda yoki saqlanganda — ota `taklif` ni tozalaydi (qayta mount bo'lganda o'z-o'zidan ochilmasligi uchun). */
  onTaklifTugadi?: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [ochiq, setOchiq] = useState(false);
  const [tur, setTur] = useState<'jarima' | 'ragbat'>('jarima');
  const [sana, setSana] = useState<Date | null>(new Date());
  const [summa, setSumma] = useState<number | string>('');
  const [sabab, setSabab] = useState('');
  const yop = () => { setOchiq(false); onTaklifTugadi?.(); };
  // Ish vaqti jadvalidan "Bonus" — rag'bat, o'sha kun va tayyor izoh bilan (summani admin yozadi)
  useEffect(() => {
    if (!taklif) return;
    setTur('ragbat');
    setSana(new Date(`${taklif.kun}T12:00:00`));
    setSumma('');
    setSabab(taklif.sabab);
    setOchiq(true);
  }, [taklif]);

  const { data: royxat = [] } = useQuery({
    queryKey: ['salary-adjustments', userId, month],
    queryFn: async () =>
      (await api.get<Tuzatish[]>('/payroll/adjustments', { params: { userId, month } })).data,
  });

  const yangila = () => {
    qc.invalidateQueries({ queryKey: ['salary-adjustments', userId, month] });
    qc.invalidateQueries({ queryKey: ['salary-compute', userId] });
    qc.invalidateQueries({ queryKey: ['payroll-summary'] });
    qc.invalidateQueries({ queryKey: ['payroll-oylik'] }); // Maosh sahifasi — Tuzatish / Yozilgan
  };
  const xato = (e: unknown) =>
    notifications.show({
      color: 'red',
      message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
    });

  const qosh = useMutation({
    mutationFn: () =>
      api.post('/payroll/adjustments', {
        userId,
        date: sana ? kunKalit(sana) : undefined,
        /* Ishora TURDAN kelib chiqadi, foydalanuvchi minus yozmaydi: qo'lda
           yozilganda "-50000" o'rniga "50000" kiritish juda oson va jarima
           rag'batga aylanib qolardi. */
        amount: tur === 'jarima' ? -Math.abs(Number(summa) || 0) : Math.abs(Number(summa) || 0),
        reason: sabab.trim(),
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('common.save') });
      yop();
      setSumma('');
      setSabab('');
      yangila();
    },
    onError: xato,
  });

  const ochir = useMutation({
    mutationFn: (id: string) => api.delete(`/payroll/adjustments/${id}`),
    onSuccess: yangila,
    onError: xato,
  });

  const jami = royxat.reduce((s, x) => s + x.amount, 0);

  return (
    <SectionCard
      title={t('staffDetail.adjustments')}
      icon={IconScale}
      right={
        <Button size="compact-sm" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setOchiq(true)}>
          {t('common.add')}
        </Button>
      }
    >
      {!royxat.length ? (
        <Text size="sm" c="dimmed">{t('staffDetail.adjustmentsEmpty')}</Text>
      ) : (
        <>
          <Table.ScrollContainer minWidth={480}>
            <Table verticalSpacing="xs">
              <Table.Tbody>
                {royxat.map((x) => (
                  <Table.Tr key={x.id}>
                    <Table.Td w={100}><Text size="sm" ff="monospace">{kunMatni(x.date)}</Text></Table.Td>
                    <Table.Td w={130}>
                      <Badge variant="light" color={x.amount < 0 ? 'red' : 'teal'}>
                        {x.amount > 0 ? '+' : ''}{formatSom(x.amount)}
                      </Badge>
                    </Table.Td>
                    <Table.Td><Text size="sm">{x.reason}</Text></Table.Td>
                    <Table.Td w={40}>
                      <ActionIcon
                        variant="subtle" color="red" size="sm"
                        loading={ochir.isPending && ochir.variables === x.id}
                        onClick={() => confirmDelete(`${kunMatni(x.date)} — ${x.reason}`, () => ochir.mutate(x.id))}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Group justify="flex-end" mt="xs">
            <Text size="sm" fw={600} c={jami < 0 ? 'red.7' : 'teal.7'}>
              {t('staffDetail.adjustmentsTotal')}: {jami > 0 ? '+' : ''}{formatSom(jami)}
            </Text>
          </Group>
        </>
      )}

      <Modal opened={ochiq} onClose={yop} title={t('staffDetail.adjustmentAdd')} centered>
        <Stack>
          <SegmentedControl
            fullWidth
            value={tur}
            onChange={(v) => setTur(v as 'jarima' | 'ragbat')}
            data={[
              { value: 'jarima', label: t('staffDetail.penalty') },
              { value: 'ragbat', label: t('staffDetail.bonus') },
            ]}
          />
          {/* Kunlik maosh KO'RSATILADI: jarima summasini "bir kunlik maoshning
              yarmi" kabi o'ylab topish oson bo'lsin. */}
          {!!kunlik && (
            <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} p="xs">
              <Text size="sm">{t('staffDetail.perDay')}: <b>{formatSom(kunlik)}</b></Text>
            </Alert>
          )}
          <DateInput {...uzDateProps} label={t('staffDetail.adjustmentDate')} value={sana} onChange={setSana} clearable={false} />
          <NumberInput
            label={t('staffDetail.amount')}
            min={0}
            thousandSeparator=" "
            value={summa}
            onChange={setSumma}
            required
          />
          <TextInput
            label={t('staffDetail.reason')}
            description={t('staffDetail.reasonHint')}
            value={sabab}
            onChange={(e) => setSabab(e.currentTarget.value)}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={yop}>{t('common.cancel')}</Button>
            <Button
              loading={qosh.isPending}
              disabled={!Number(summa) || !sabab.trim()}
              color={tur === 'jarima' ? 'red' : 'teal'}
              onClick={() => qosh.mutate()}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </SectionCard>
  );
}
